import type {ApiImageryCheckoutPreview} from 'app/modules/Remote/api';
import type {ApiImageryPrepaid} from 'app/modules/Remote/Organization';

import type {CheckoutAttempt} from './payment';
import {isCoveredByCredit, prepaidRemainingCents} from './prepaidCredit';

/**
 * What a pay-now cart will charge on top of its scenes, summed over its groups.
 * `estimated` is a cart whose charge can't be known until checkout applies the credit: the
 * credit it gets is at most `prepaidUpToCents`.
 */
export type CartCharges =
  | {status: 'loading'}
  | {status: 'failed'}
  | {status: 'estimated'; prepaidUpToCents: number}
  | {
      status: 'ready';
      orderFeeCents: number;
      orderMinimumCents: number;
      /** The minimum a group's scenes are topped up to. */
      orderMinimumThresholdCents: number;
      /** Prepaid credit taken off the scenes, before tax. */
      prepaidCents: number;
      taxCents: number;
      totalCents: number;
    };

/** How a group's checkout is paid. `checking` waits on the balance or the previews to say. */
type GroupPayment = 'card' | 'credit' | 'checking';

interface PreviewState {
  data?: ApiImageryCheckoutPreview;
  isPending: boolean;
  isError: boolean;
}

export interface ChargeGroup {
  key: string;
  /** What the group's checkout is for, as `checkoutContents` identifies it. */
  contents: string;
  subtotalCents: number;
  /** Its contract hasn't loaded, so it can't be priced yet. */
  isLoading: boolean;
  /** An attempt still waiting on the server under the group's key. */
  waiting: CheckoutAttempt | undefined;
  /** Absent for a group with no contract, which isn't priced. */
  preview: PreviewState | undefined;
}

/** The organization's prepaid credit. `granted` says whether it was ever given any. */
export type CreditBalance =
  | {status: 'pending'}
  | {status: 'error'}
  | {status: 'success'; remainingCents: number; granted: boolean};

/** The balance a prepaids query holds. A refetch that fails keeps the balance it had. */
export const creditBalanceOf = (query: {
  data?: ApiImageryPrepaid[];
  isError: boolean;
}): CreditBalance =>
  query.data
    ? {
        status: 'success',
        remainingCents: prepaidRemainingCents(query.data),
        granted: query.data.length > 0,
      }
    : query.isError
      ? {status: 'error'}
      : {status: 'pending'};

/**
 * Where a group's payment comes from: an attempt resent as it is, on its card or with no card; a
 * card the server asked for this order; or, for a `fresh` group, its preview and the balance.
 */
type GroupStanding = 'resumesCard' | 'resumesCredit' | 'asked' | 'fresh';

function standingOf(group: ChargeGroup, cardRequests: ReadonlyMap<string, string>): GroupStanding {
  const {waiting, contents} = group;
  if (waiting?.confirmationTokenId !== undefined) {
    return 'resumesCard';
  }
  if (waiting && waiting.cardNeededFor === undefined) {
    return 'resumesCredit';
  }
  return cardRequests.get(group.key) === contents || waiting?.cardNeededFor === contents
    ? 'asked'
    : 'fresh';
}

// Groups with no attempt, and the ones the server asked a card for, are priced from previews that
// each draw on the whole balance, so together they may draw more than is left.
const isPricedByPreview = (standing: GroupStanding | undefined) =>
  standing === 'fresh' || standing === 'asked';

export interface CartPaymentPlan {
  payments: ReadonlyMap<string, GroupPayment>;
  charges: CartCharges;
}

/**
 * How each group of a pay-now cart is paid, and what the cart can say it costs.
 *
 * A group with an attempt waiting is resent as it is. Its preview and the balance leave out the
 * credit the attempt holds, and the server says when the attempt needs a card. Once the server has
 * asked a card for an attempt, the attempt is judged like a group without one. The fresh groups pay
 * with credit alone only when every one of them is covered and, since each preview draws on the
 * whole balance, what they draw together with the groups asked a card for fits in what's left.
 *
 * `cardRequests` holds, by group, the order the server asked a card for when it gave up the key.
 */
export function planCartPayment({
  groups,
  cardRequests,
  balance,
  budgetFailed,
}: {
  groups: ChargeGroup[];
  cardRequests: ReadonlyMap<string, string>;
  balance: CreditBalance;
  budgetFailed: boolean;
}): CartPaymentPlan {
  const standings = new Map<string, GroupStanding>();
  const payments = new Map<string, GroupPayment>();
  const fresh: ChargeGroup[] = [];
  for (const group of groups) {
    const standing = standingOf(group, cardRequests);
    standings.set(group.key, standing);
    if (standing === 'fresh') {
      fresh.push(group);
    } else {
      payments.set(group.key, standing === 'resumesCredit' ? 'credit' : 'card');
    }
  }

  const previewDrawCents = groups.reduce(
    (total, group) =>
      isPricedByPreview(standings.get(group.key))
        ? total + (group.preview?.data?.prepaidCents ?? 0)
        : total,
    0
  );
  const freshPayment = ((): GroupPayment => {
    if (balance.status === 'pending') {
      return 'checking';
    }
    if (balance.status === 'error' || balance.remainingCents === 0) {
      return 'card';
    }
    if (fresh.some((group) => group.isLoading || group.preview?.isPending)) {
      return 'checking';
    }
    const covered = fresh.every(
      (group) => group.preview?.data !== undefined && isCoveredByCredit(group.preview.data)
    );
    return covered && previewDrawCents <= balance.remainingCents ? 'credit' : 'card';
  })();
  for (const group of fresh) {
    payments.set(group.key, freshPayment);
  }

  return {
    payments,
    charges: cartCharges(groups, standings, payments, balance, previewDrawCents, budgetFailed),
  };
}

function cartCharges(
  groups: ChargeGroup[],
  standings: ReadonlyMap<string, GroupStanding>,
  payments: ReadonlyMap<string, GroupPayment>,
  balance: CreditBalance,
  previewDrawCents: number,
  budgetFailed: boolean
): CartCharges {
  // Groups stay loading without a budget, so this is checked first or the totals spin for good.
  if (budgetFailed) {
    return {status: 'failed'};
  }
  const previews = groups.flatMap((group) => (group.preview ? [group.preview] : []));
  if (
    groups.some((group) => group.isLoading) ||
    previews.some((preview) => preview.isPending) ||
    [...payments.values()].includes('checking') ||
    // Whether a card attempt's invoice may hold credit is the balance's to say.
    (balance.status === 'pending' && [...standings.values()].includes('resumesCard'))
  ) {
    return {status: 'loading'};
  }
  if (previews.some((preview) => preview.isError || preview.data === undefined)) {
    return {status: 'failed'};
  }

  // A waiting attempt's invoice may have drawn credit that the balance no longer shows.
  const mayHoldCredit = balance.status !== 'success' || balance.granted;
  const overdrawn = balance.status === 'success' && previewDrawCents > balance.remainingCents;

  const sums = {
    orderFeeCents: 0,
    orderMinimumCents: 0,
    orderMinimumThresholdCents: 0,
    prepaidCents: 0,
    taxCents: 0,
    totalCents: 0,
  };
  let isEstimate = false;
  // Together the groups priced from previews can draw no more than what's left.
  let prepaidUpToCents = overdrawn ? balance.remainingCents : 0;
  for (const group of groups) {
    const data = group.preview?.data;
    if (!data) {
      continue;
    }
    const standing = standings.get(group.key);
    const drawCents = data.prepaidCents ?? 0;
    if (standing === 'resumesCredit') {
      // Credit alone pays it, so it draws every scene's price and leaves nothing to pay.
      sums.prepaidCents += group.subtotalCents;
      prepaidUpToCents += group.subtotalCents;
    } else if (standing === 'resumesCard' && mayHoldCredit) {
      isEstimate = true;
      prepaidUpToCents += group.subtotalCents;
    } else if (isPricedByPreview(standing) && overdrawn) {
      isEstimate = true;
    } else if (standing === 'asked' && isCoveredByCredit(data)) {
      // The server asked a card for an order this preview still says credit covers.
      isEstimate = true;
      prepaidUpToCents += drawCents;
    } else if (isPricedByPreview(standing) && balance.status !== 'success' && drawCents > 0) {
      // With no balance to check it against, the credit this preview draws may be gone by checkout.
      isEstimate = true;
      prepaidUpToCents += drawCents;
    } else {
      sums.orderFeeCents += data.orderFeeCents;
      sums.orderMinimumCents += data.orderMinimumCents;
      sums.orderMinimumThresholdCents = data.orderMinimumThresholdCents;
      sums.prepaidCents += drawCents;
      sums.taxCents += data.taxCents;
      sums.totalCents += data.totalCents;
      prepaidUpToCents += drawCents;
    }
  }

  return isEstimate ? {status: 'estimated', prepaidUpToCents} : {status: 'ready', ...sums};
}
